import classNames from "classnames";
import {
  addMapMarkers,
  clearMapMarkers,
  GeoLocation,
  initializeMap,
  loadMapbox,
  updateCurrentPositionPin
} from "../Mapbox/mapbox";
import { getPopover, initializePopovers } from "../Shared/Popover/popover";
import { notEmpty } from "../Shared/Utilities/filters";
import { isNullOrWhiteSpace } from "../Shared/Utilities/string";

declare global {
  interface Window {
    ani_mapboxLoaded?: boolean;
    ani_findClinicMaps?: Map<string, FindClinicMap>;
  }
}

type FindClinicMap = {
  updateClinics: () => void;
  updateCurrentPosition: (position: GeoLocation) => void;
};

type Clinic = {
  contentId: string;
  id: number;
  name: string;
  region?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  /**
   * 0: Clinic
   * 1: EmergencyClinic
   * 2: ReferralClinic
   * 4: Online
   */
  type: number[];
  url: string;
};

if (!window.ani_findClinicMaps) {
  window.ani_findClinicMaps = new Map();
}

const findClinicsMaps = window.ani_findClinicMaps;

const selectedMarkerPanOffsetY = 112;

const getMap = (mapId: string) => {
  const map = findClinicsMaps.get(mapId);

  if (!map) {
    throw `Map with id does not exist ${mapId}`;
  }

  return map;
};

export const initializeFindClinicMap = (rootElement: HTMLElement) => {
  if (rootElement.dataset.findClinicMapInitialized) {
    return;
  }
  rootElement.dataset.findClinicMapInitialized = "true";

  let currentPosition: GeoLocation | undefined = undefined;

  const mapCollapsible = rootElement.querySelector<HTMLElement>(
    "[data-find-clinic-map-collapsible]"
  );

  const mapContainer = rootElement.querySelector<HTMLElement>(
    "[data-find-clinic-map-container]"
  );

  const mapViewport = mapContainer?.parentElement;

  const mapButton = document.querySelector<HTMLElement>(
    `[aria-controls="${mapCollapsible?.id}"]`
  );

  if (!mapCollapsible) {
    throw "Find clinic map collapsible missing";
  }

  if (!mapContainer) {
    throw "Find clinic map container missing";
  }

  if (!mapViewport) {
    throw "Find clinic map viewport missing";
  }

  if (!mapButton) {
    throw "Find clinic mapButton missing";
  }

  const mapboxAccessToken = rootElement.dataset.accessToken;

  const mapboxLanguage = rootElement.dataset.language;

  const mapId = rootElement.id;

  const defaultLatitude = !isNullOrWhiteSpace(rootElement.dataset.defaultLatitude)
    ? parseFloat(rootElement.dataset.defaultLatitude)
    : 0;

  const defaultLongitude = !isNullOrWhiteSpace(rootElement.dataset.defaultLongitude)
    ? parseFloat(rootElement.dataset.defaultLongitude)
    : 0;

  const userLatitude = !isNullOrWhiteSpace(rootElement.dataset.userLatitude)
    ? parseFloat(rootElement.dataset.userLatitude)
    : null;

  const userLongitude = !isNullOrWhiteSpace(rootElement.dataset.userLongitude)
    ? parseFloat(rootElement.dataset.userLongitude)
    : null;

  const defaultZoom = !isNullOrWhiteSpace(rootElement.dataset.defaultZoom)
    ? parseInt(rootElement.dataset.defaultZoom)
    : 0;

  if (!mapId) {
    throw "Find clinic mapId missing";
  }

  const popoverAnchor = document.getElementById(`${mapId}-popover-anchor`);

  if (!popoverAnchor) {
    throw "Find clinic map popover anchor missing";
  }

  if (!mapboxAccessToken) {
    throw "Find clinic page mapboxAccessToken missing";
  }

  loadMapbox(mapboxAccessToken);

  let mapInitialized = false;

  let selectedMarkerButton: HTMLElement | undefined = undefined;

  let clinicsJson = document.querySelector("[data-clinics-json]")?.textContent;

  let clinics = clinicsJson ? (JSON.parse(clinicsJson) as Clinic[]) : undefined;

  if (userLatitude !== null && userLongitude !== null) {
    currentPosition = {
      latitude: userLatitude,
      longitude: userLongitude
    };
  }

  const updateSelectedPopoverPosition = (repositionClosedPopover = false) => {
    if (!selectedMarkerButton) {
      return;
    }

    const markerRect = selectedMarkerButton.getBoundingClientRect();
    const viewportRect = mapViewport.getBoundingClientRect();

    const anchorLeft = markerRect.left - viewportRect.left + markerRect.width / 2;
    const anchorTop = markerRect.top - viewportRect.top + markerRect.height / 2;

    popoverAnchor.style.left = `${anchorLeft}px`;
    popoverAnchor.style.top = `${anchorTop}px`;

    const popoverId = selectedMarkerButton.getAttribute("aria-controls");
    const popoverElement = popoverId ? document.getElementById(popoverId) : undefined;
    const popover =
      popoverElement instanceof HTMLElement ? getPopover(popoverElement) : undefined;

    if (popover && (popover.isOpen() || repositionClosedPopover)) {
      popover.reposition();
    }
  };

  const updateMapMarkers = () => {
    if (clinics) {
      clearMapMarkers(mapId);
      selectedMarkerButton = undefined;

      const markerDataContainer = document.getElementById("find-clinic-map-marker-data");
      const clinicIconHref =
        markerDataContainer?.dataset.iconHref ?? "/svg/system/clinic.svg#icon";

      const markers = clinics.map(clinic => {
        const { latitude, longitude } = clinic.coordinates ?? {};

        if (
          latitude !== undefined &&
          latitude !== 0 &&
          longitude !== undefined &&
          longitude !== 0
        ) {
          const btn = document.createElement("button");
          btn.id = `clinic-marker-${clinic.id}`;
          btn.type = "button";
          // transition-short-in-out includes 'transform' which breaks mapbox marker syncing
          // We apply the other transition properties explicitly or rely on the override in mapbox.ts
          btn.className = classNames(
            "absolute top-0 left-0",
            "flex flex-col justify-center items-center gap-[0.0625rem] w-2xl h-2xl",
            "text-neutral-0 bg-dark-blue-600 rounded-full shadow-md",
            "hover:bg-dark-blue-400",
            "focus-outline [*:focus-visible_&]:outline-4 [*:focus-visible_&]:outline-dark-blue-400",
            "aria-expanded:outline-4 aria-expanded:outline-turquoise-800"
          );

          // Mapbox uses CSS translate() for positioning. If we don't clear transition on transform,
          // the marker visually lags during dragging.
          btn.style.transitionProperty =
            "background-color, color, border-color, box-shadow, outline-color, opacity, visibility, text-decoration-color";
          btn.style.transitionDuration = "250ms";
          btn.style.transitionTimingFunction = "ease-in-out";
          btn.setAttribute("aria-controls", `clinic-popover-${clinic.id}`);
          btn.setAttribute("aria-haspopup", "dialog");
          btn.addEventListener("click", () => {
            selectedMarkerButton = btn;
            updateSelectedPopoverPosition(true);
          });

          const hasEmergency = clinic.type && clinic.type.includes(1);
          const hasReferral = clinic.type && clinic.type.includes(2);

          btn.innerHTML = `
              <svg aria-hidden="true" focusable="false" class="flex-none inline-flex justify-center items-center w-sm h-sm">
                  <use href="${clinicIconHref}"></use>
              </svg>
              <div class="flex gap-[0.125rem] justify-center">
                  ${hasEmergency ? '<span class="flex-none w-[0.375rem] h-[0.375rem] rounded-full bg-pink-800"></span>' : ""}
                  <span class="flex-none w-[0.375rem] h-[0.375rem] rounded-full bg-turquoise-800"></span>
                  ${hasReferral ? '<span class="flex-none w-[0.375rem] h-[0.375rem] rounded-full bg-petrol-800"></span>' : ""}
              </div>
          `;

          return {
            element: btn,
            panOffset: { y: selectedMarkerPanOffsetY },
            position: { lat: latitude, lng: longitude },
            title: clinic.name
          };
        }
      });

      addMapMarkers(mapId, markers.filter(notEmpty));
    }
  };

  mapButton.addEventListener("click", () => {
    if (!mapInitialized) {
      mapInitialized = true;

      const map = initializeMap(
        mapContainer,
        mapId,
        defaultLatitude,
        defaultLongitude,
        defaultZoom
      );

      map.on("load", () => {
        // Markers must be created and added to the map before initializing popovers,
        // because popover.ts looks for the [aria-controls] button in the DOM.
        updateMapMarkers();
        initializePopovers(document.body);
        map.on("move", () => updateSelectedPopoverPosition());

        if (currentPosition && currentPosition.latitude && currentPosition.longitude) {
          updateCurrentPositionPin(mapId, {
            lat: currentPosition.latitude,
            lng: currentPosition.longitude
          });
        }
      });
    }
  });

  findClinicsMaps.set(mapId, {
    updateCurrentPosition: (position: GeoLocation) => {
      currentPosition = position;

      if (mapInitialized) {
        updateCurrentPositionPin(mapId, {
          lat: currentPosition.latitude,
          lng: currentPosition.longitude
        });
      }
    },
    updateClinics: () => {
      clinicsJson = document.querySelector("[data-clinics-json]")?.textContent;
      clinics = clinicsJson ? (JSON.parse(clinicsJson) as Clinic[]) : undefined;

      if (mapInitialized) {
        // Markers must be created and added to the map before initializing popovers,
        // because popover.ts looks for the [aria-controls] button in the DOM.
        updateMapMarkers();
        initializePopovers(document.body);
      }
    }
  });
};

export const updateCurrentPosition = (mapId: string, position: GeoLocation) => {
  const map = getMap(mapId);
  map.updateCurrentPosition(position);
};

export const updateClinics = (mapId: string) => {
  const map = getMap(mapId);
  map.updateClinics();
};

document
  .querySelectorAll<HTMLElement>("[data-find-clinic-map]")
  .forEach(initializeFindClinicMap);
